import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box, Typography, Card, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Alert, Tooltip,
  Autocomplete, Divider, List, ListItem, ListItemText, ListItemAvatar, Avatar,
  useTheme, useMediaQuery
} from '@mui/material';
import { Add, Edit, Delete, Group, PersonAdd, RemoveCircleOutline, AccessTime, EditCalendar, SupervisedUserCircle, WorkHistory, Flag } from '@mui/icons-material';
import { projectsAPI, clientsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SlideTransition from '../../components/common/SlideTransition';
import DevBadge from '../../components/common/DevBadge';

const MILESTONE_STATUS_LABELS = { active: 'Activo', completed: 'Completado', cancelled: 'Cancelado' };
const MILESTONE_STATUS_COLORS = { active: 'primary', completed: 'success', cancelled: 'default' };

const STATUS_LABELS = { active: 'Activo', paused: 'En pausa', completed: 'Terminado' };
const STATUS_COLORS = { active: 'success', paused: 'warning', completed: 'default' };

// ─── ISO week helpers ─────────────────────────────────────────────────────
const getISOWeekNum = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// ISO week year (differs from calendar year for weeks straddling Jan 1)
const getISOWeekYear = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  return d.getUTCFullYear();
};

// pastWeeks: how many weeks back to show; futureWeeks: how many ahead (default ~1 year)
const getWeekOptions = (pastWeeks = 8, futureWeeks = 56) => {
  const now = new Date();
  const day = now.getDay() || 7;
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - day + 1);
  thisMonday.setHours(0, 0, 0, 0);
  const thisMondayStr = thisMonday.toISOString().slice(0, 10);

  const fmt = (d) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

  const options = [];
  for (let i = -pastWeeks; i <= futureWeeks; i++) {
    const monday = new Date(thisMonday);
    monday.setDate(thisMonday.getDate() + i * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const mondayStr = monday.toISOString().slice(0, 10);
    options.push({
      value: mondayStr,
      weekNum: getISOWeekNum(monday),
      isoYear: getISOWeekYear(monday),
      rangeLabel: `${fmt(monday)} – ${fmt(sunday)}`,
      isCurrent: mondayStr === thisMondayStr
    });
  }
  return options;
};

export default function ProjectsManagement() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'teamlead';
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [editProj, setEditProj] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', clientId: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState({ open: false, projId: null });

  // Members dialog
  const [membersDialog, setMembersDialog] = useState({ open: false, project: null });
  const [memberEmail, setMemberEmail] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const memberSearchTimer = React.useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Weekly hours dialog
  const [weeklyDialog, setWeeklyDialog] = useState({ open: false, project: null });
  const [weeklyForm, setWeeklyForm] = useState({ hours: 40, effectiveFrom: '', note: '' });
  const [weeklySaving, setWeeklySaving] = useState(false);
  const [weeklyError, setWeeklyError] = useState('');

  // Work schedule dialog
  const [wsDialog, setWsDialog] = useState({ open: false, project: null });
  const [wsForm, setWsForm] = useState({ workDays: [1, 2, 3, 4, 5], startTime: '08:00', endTime: '18:00' });
  const [wsSaving, setWsSaving] = useState(false);
  const [wsError, setWsError] = useState('');

  // Client leads dialog
  const [clientLeadsDialog, setClientLeadsDialog] = useState({ open: false, project: null });
  const [clOptions, setClOptions] = useState([]);
  const clSearchTimer = React.useRef(null);
  const [clError, setClError] = useState('');

  // Milestones dialog
  const empty_msForm = { name: '', description: '', status: 'active', dueDate: '' };
  const [milestonesDialog, setMilestonesDialog] = useState({ open: false, project: null });
  const [msEditTarget, setMsEditTarget] = useState(null);
  const [msForm, setMsForm] = useState(empty_msForm);
  const [msSaving, setMsSaving] = useState(false);
  const [msError, setMsError] = useState('');
  const [msConfirm, setMsConfirm] = useState({ open: false, milestoneId: null });

  const load = async () => {
    try {
      const [prjRes, clientRes] = await Promise.all([projectsAPI.getAll(), clientsAPI.getAll()]);
      setProjects(prjRes.data);
      setClients(clientRes.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleOpen = (proj = null) => {
    setEditProj(proj);
    setForm(proj
      ? { name: proj.name, description: proj.description || '', clientId: proj.clientId?._id?.toString() || proj.clientId?.toString() || '', status: proj.status }
      : { name: '', description: '', clientId: '', status: 'active' }
    );
    setError('');
    setOpenDialog(true);
  };

  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      if (editProj) await projectsAPI.update(editProj._id, form);
      else await projectsAPI.create(form);
      setOpenDialog(false); load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await projectsAPI.delete(id);
      setConfirm({ open: false, projId: null }); load();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  // Members
  const openMembers = async (proj) => {
    setMembersDialog({ open: true, project: proj });
    setMemberEmail('');
    try {
      const res = await usersAPI.search('', 'member');
      setMemberOptions(res.data);
    } catch { }
  };

  const handleMemberSearch = (q) => {
    clearTimeout(memberSearchTimer.current);
    memberSearchTimer.current = setTimeout(async () => {
      try {
        const res = await usersAPI.search(q, 'member');
        setMemberOptions(res.data);
      } catch { }
    }, 300);
  };

  const handleAddMember = async (user) => {
    if (!user) return;
    try {
      await projectsAPI.addMember(membersDialog.project._id, { userId: user._id });
      const updated = await projectsAPI.getById(membersDialog.project._id);
      setMembersDialog(d => ({ ...d, project: updated.data }));
      load();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await projectsAPI.removeMember(membersDialog.project._id, userId);
      const updated = await projectsAPI.getById(membersDialog.project._id);
      setMembersDialog(d => ({ ...d, project: updated.data }));
      load();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  // Work schedule handlers
  const openWorkSchedule = (proj) => {
    setWsDialog({ open: true, project: proj });
    const ws = proj.workSchedule;
    setWsForm(ws?.startTime
      ? { workDays: ws.workDays || [], startTime: ws.startTime, endTime: ws.endTime }
      : { workDays: [1, 2, 3, 4, 5], startTime: '08:00', endTime: '18:00' }
    );
    setWsError('');
  };

  const handleSaveWorkSchedule = async () => {
    setWsError(''); setWsSaving(true);
    try {
      await projectsAPI.setWorkSchedule(wsDialog.project._id, wsForm);
      setWsDialog({ open: false, project: null });
      load();
    } catch (err) {
      setWsError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Error al guardar');
    } finally { setWsSaving(false); }
  };

  // Weekly hours helpers
  const getMonday = () => {
    const d = new Date();
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - day + 1);
    return d.toISOString().slice(0, 10);
  };

  const openWeeklyHours = (proj) => {
    setWeeklyDialog({ open: true, project: proj });
    setWeeklyForm({ hours: 40, effectiveFrom: getMonday(), note: '' });
    setWeeklyError('');
  };

  const handleSaveWeeklyHours = async () => {
    setWeeklyError(''); setWeeklySaving(true);
    try {
      await projectsAPI.setWeeklyHours(weeklyDialog.project._id, weeklyForm);
      const updated = await projectsAPI.getById(weeklyDialog.project._id);
      setWeeklyDialog(d => ({ ...d, project: updated.data }));
      load();
    } catch (err) {
      setWeeklyError(err.response?.data?.error || 'Error al guardar');
    } finally { setWeeklySaving(false); }
  };

  // Client leads helpers
  const openClientLeads = async (proj) => {
    setClientLeadsDialog({ open: true, project: proj });
    setClError('');
    try {
      const res = await usersAPI.search('', 'clientlead');
      setClOptions(res.data);
    } catch { }
  };

  const handleClSearch = (q) => {
    clearTimeout(clSearchTimer.current);
    clSearchTimer.current = setTimeout(async () => {
      try {
        const res = await usersAPI.search(q, 'clientlead');
        setClOptions(res.data);
      } catch { }
    }, 300);
  };

  const handleAddClientLead = async (cl) => {
    if (!cl) return;
    setClError('');
    try {
      await projectsAPI.assignClientLead(clientLeadsDialog.project._id, { userId: cl._id });
      const updated = await projectsAPI.getById(clientLeadsDialog.project._id);
      setClientLeadsDialog(d => ({ ...d, project: updated.data }));
      load();
    } catch (err) { setClError(err.response?.data?.error || 'Error'); }
  };

  const handleRemoveClientLead = async (userId) => {
    setClError('');
    try {
      await projectsAPI.removeClientLead(clientLeadsDialog.project._id, userId);
      const updated = await projectsAPI.getById(clientLeadsDialog.project._id);
      setClientLeadsDialog(d => ({ ...d, project: updated.data }));
      load();
    } catch (err) { setClError(err.response?.data?.error || 'Error'); }
  };

  // Milestones handlers
  const openMilestones = (proj) => {
    setMilestonesDialog({ open: true, project: proj });
    setMsEditTarget(null);
    setMsForm(empty_msForm);
    setMsError('');
  };

  const startEditMilestone = (ms) => {
    setMsEditTarget(ms);
    setMsForm({
      name: ms.name,
      description: ms.description || '',
      status: ms.status,
      dueDate: ms.dueDate ? ms.dueDate.slice(0, 10) : ''
    });
    setMsError('');
  };

  const cancelMsEdit = () => {
    setMsEditTarget(null);
    setMsForm(empty_msForm);
    setMsError('');
  };

  const handleSaveMilestone = async () => {
    if (!msForm.name.trim()) { setMsError('El nombre es requerido'); return; }
    setMsSaving(true); setMsError('');
    try {
      const payload = { ...msForm, dueDate: msForm.dueDate || null };
      if (msEditTarget) {
        await projectsAPI.updateMilestone(milestonesDialog.project._id, msEditTarget._id, payload);
      } else {
        await projectsAPI.addMilestone(milestonesDialog.project._id, payload);
      }
      const updated = await projectsAPI.getById(milestonesDialog.project._id);
      setMilestonesDialog(d => ({ ...d, project: updated.data }));
      setMsEditTarget(null);
      setMsForm(empty_msForm);
      load();
    } catch (err) {
      setMsError(err.response?.data?.error || 'Error al guardar');
    } finally { setMsSaving(false); }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    try {
      await projectsAPI.deleteMilestone(milestonesDialog.project._id, milestoneId);
      const updated = await projectsAPI.getById(milestonesDialog.project._id);
      setMilestonesDialog(d => ({ ...d, project: updated.data }));
      setMsConfirm({ open: false, milestoneId: null });
      load();
    } catch (err) { setMsError(err.response?.data?.error || 'Error'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <DevBadge id="page:teamlead-proyectos" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Gestión de Proyectos</Typography>
          <Typography variant="body2" color="text.secondary">Administra tus proyectos y equipos</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}
          sx={{ display: isAdmin ? undefined : 'none' }}
        >Nuevo Proyecto</Button>
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Proyecto</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Miembros</TableCell>
                <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map(proj => {
                const client = clients.find(c => c._id === (proj.clientId?._id?.toString() || proj.clientId?.toString()));
                return (
                  <TableRow key={proj._id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>{proj.name}</Typography>
                      {proj.description && <Typography variant="caption" color="text.secondary">{proj.description}</Typography>}
                    </TableCell>
                    <TableCell>{client?.name || proj.clientId?.name || '—'}</TableCell>
                    <TableCell>
                      <Chip icon={<Group />} label={proj.members?.length ?? 0} size="small" variant="outlined"
                        onClick={() => openMembers(proj)} clickable />
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABELS[proj.status] || proj.status} size="small" color={STATUS_COLORS[proj.status]} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                        <Tooltip title="Ver actividades">
                          <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }}
                            onClick={() => navigate(`/${user?.role === 'clientlead' ? 'clientlead' : 'teamlead'}/activities?projectId=${proj._id}`)}>
                            <AccessTime fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hitos">
                          <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openMilestones(proj)}>
                            <Flag fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isAdmin && (
                          <>
                            <Tooltip title="Gestionar miembros">
                              <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openMembers(proj)}><Group fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Horas semanales">
                              <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openWeeklyHours(proj)}>
                                <EditCalendar fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Horario de trabajo">
                              <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openWorkSchedule(proj)}>
                                <WorkHistory fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Líderes Cliente">
                              <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openClientLeads(proj)}>
                                <SupervisedUserCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => handleOpen(proj)}><Edit fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" color="error" sx={{ borderRadius: '5px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }} onClick={() => setConfirm({ open: true, projId: proj._id })}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {projects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>No hay proyectos aún.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          {editProj ? 'Editar Proyecto' : 'Nuevo Proyecto'}
          <DevBadge id="modal:proyecto" inline />
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Nombre del proyecto *" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Descripción" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              multiline rows={3} fullWidth />
            <TextField select label="Cliente" value={form.clientId}
              onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))} fullWidth>
              <MenuItem value="">Sin cliente</MenuItem>
              {clients.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select label="Estado" value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))} fullWidth>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={membersDialog.open} onClose={() => setMembersDialog({ open: false, project: null })} maxWidth="sm" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Miembros — {membersDialog.project?.name}
          <DevBadge id="modal:miembros-proyecto" inline />
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Autocomplete
              options={memberOptions.filter(u =>
                !(membersDialog.project?.members || []).some(m => {
                  const mid = m.userId?._id?.toString() || m.userId?.toString();
                  return mid === u._id || m._id === u._id;
                })
              )}
              getOptionLabel={o => o.name ? `${o.name} ${o.lastName || ''}`.trim() + ` (${o.email})` : o.email}
              filterOptions={x => x}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              onInputChange={(_, val) => handleMemberSearch(val)}
              onChange={(_, val) => { if (val) handleAddMember(val); }}
              renderInput={params => (
                <TextField {...params} label="Buscar y agregar usuario" placeholder="Nombre o correo..." />
              )}
              noOptionsText="Sin resultados"
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          <List dense>
            {(membersDialog.project?.members || []).map(m => {
              const u = m.userId && typeof m.userId === 'object' ? m.userId : null;
              const displayName = u?.name ? `${u.name} ${u.lastName || ''}`.trim() : (m.name || null);
              const displayEmail = u?.email || m.email || '';
              const displayPhone = u?.phone || m.phone || null;
              const avatarChar = (displayName || displayEmail || '?')[0].toUpperCase();
              const memberId = (u?._id || m.userId)?.toString();
              return (
                <ListItem key={memberId || m.email}
                  secondaryAction={
                    <Tooltip title="Quitar del proyecto">
                      <IconButton edge="end" size="small" color="error"
                        onClick={() => handleRemoveMember(memberId)}>
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }>
                  <ListItemAvatar>
                    <Tooltip title={displayPhone ? `Tel: ${displayPhone}` : 'Sin teléfono registrado'} arrow>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a1a1a', fontSize: 14, cursor: 'default' }}>
                        {avatarChar}
                      </Avatar>
                    </Tooltip>
                  </ListItemAvatar>
                  <ListItemText
                    primary={displayName || displayEmail}
                    secondary={displayName ? displayEmail : null}
                  />
                </ListItem>
              );
            })}
            {(!membersDialog.project?.members?.length) && (
              <ListItem><ListItemText secondary="Sin miembros aún." /></ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMembersDialog({ open: false, project: null })}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open} title="Eliminar Proyecto"
        message="¿Eliminar este proyecto? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        onConfirm={() => handleDelete(confirm.projId)}
        onCancel={() => setConfirm({ open: false, projId: null })}
      />

      {/* Work Schedule Dialog */}
      <Dialog open={wsDialog.open} onClose={() => setWsDialog({ open: false, project: null })} maxWidth="sm" fullWidth TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Horario de Trabajo — {wsDialog.project?.name}
          <DevBadge id="modal:horario-trabajo" inline />
        </DialogTitle>
        <DialogContent>
          {wsError && <Alert severity="error" sx={{ mb: 2 }}>{wsError}</Alert>}
          {wsDialog.project?.workSchedule?.startTime && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Horario actual: {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom']
                .filter((_, i) => wsDialog.project.workSchedule.workDays?.includes(i + 1))
                .join(', ')} · {wsDialog.project.workSchedule.startTime} – {wsDialog.project.workSchedule.endTime}
            </Alert>
          )}
          <Typography variant="subtitle2" gutterBottom>Días laborales</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
            {[{ label: 'Lun', value: 1 }, { label: 'Mar', value: 2 }, { label: 'Mié', value: 3 },
              { label: 'Jue', value: 4 }, { label: 'Vie', value: 5 }, { label: 'Sáb', value: 6 }, { label: 'Dom', value: 7 }
            ].map(day => (
              <Chip key={day.value} label={day.label} clickable
                color={wsForm.workDays.includes(day.value) ? 'primary' : 'default'}
                onClick={() => setWsForm(f => ({
                  ...f,
                  workDays: f.workDays.includes(day.value)
                    ? f.workDays.filter(d => d !== day.value)
                    : [...f.workDays, day.value].sort((a, b) => a - b)
                }))}
              />
            ))}
          </Box>
          <Typography variant="subtitle2" gutterBottom>Rango horario normal</Typography>
          <Box display="flex" gap={2} mt={1}>
            <TextField
              label="Hora de inicio *"
              type="time"
              value={wsForm.startTime}
              onChange={e => setWsForm(f => ({ ...f, startTime: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Hora de fin *"
              type="time"
              value={wsForm.endTime}
              onChange={e => setWsForm(f => ({ ...f, endTime: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Alert severity="info" sx={{ mt: 2 }} icon={false}>
            <Typography variant="caption">
              Se considera <strong>hora extra</strong> cualquier actividad cuya hora de fin supere el horario de jornada, o que se registre en un día no laboral. Se requerirá evidencia de aprobación.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWsDialog({ open: false, project: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveWorkSchedule}
            disabled={wsSaving || wsForm.workDays.length === 0 || !wsForm.startTime || !wsForm.endTime}>
            {wsSaving ? 'Guardando...' : 'Guardar horario'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Weekly Hours Dialog */}
      <Dialog open={weeklyDialog.open} onClose={() => setWeeklyDialog({ open: false, project: null })} maxWidth="sm" fullWidth TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Horas Semanales — {weeklyDialog.project?.name}
          <DevBadge id="modal:horas-semanales" inline />
        </DialogTitle>
        <DialogContent>
          {weeklyError && <Alert severity="error" sx={{ mb: 2 }}>{weeklyError}</Alert>}

          {/* History */}
          {(weeklyDialog.project?.weeklyHoursConfig?.length > 0) && (
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Historial de configuración</Typography>
              <List dense disablePadding>
                {[...(weeklyDialog.project.weeklyHoursConfig || [])]
                  .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))
                  .map((cfg, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={`${cfg.hours}h / semana`}
                        secondary={`Sem. ${getISOWeekNum(new Date(cfg.effectiveFrom))} · ${dayjs(cfg.effectiveFrom).format('DD/MM')} – ${dayjs(cfg.effectiveFrom).add(6, 'day').format('DD/MM/YYYY')}${cfg.note ? ' · ' + cfg.note : ''}`}
                      />
                    </ListItem>
                  ))}
              </List>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          <Typography variant="subtitle2" gutterBottom>Nueva configuración</Typography>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Horas por semana *"
              type="number"
              inputProps={{ min: 1, max: 168, step: 0.5 }}
              value={weeklyForm.hours}
              onChange={e => setWeeklyForm(f => ({ ...f, hours: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Vigente desde (semana) *"
              value={weeklyForm.effectiveFrom}
              onChange={e => setWeeklyForm(f => ({ ...f, effectiveFrom: e.target.value }))}
              fullWidth
            >
              {getWeekOptions(0, 56).map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box>
                    <Typography variant="body2" fontWeight={opt.isCurrent ? 700 : 400}>
                      Semana {opt.weekNum} / {opt.isoYear}{opt.isCurrent ? ' — semana actual' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{opt.rangeLabel}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Nota (opcional)"
              value={weeklyForm.note}
              onChange={e => setWeeklyForm(f => ({ ...f, note: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWeeklyDialog({ open: false, project: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveWeeklyHours}
            disabled={weeklySaving || !weeklyForm.hours || !weeklyForm.effectiveFrom}>
            {weeklySaving ? 'Guardando...' : 'Agregar configuración'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Leads Dialog */}
      <Dialog open={clientLeadsDialog.open} onClose={() => setClientLeadsDialog({ open: false, project: null })} maxWidth="sm" fullWidth TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Líderes Cliente — {clientLeadsDialog.project?.name}
          <DevBadge id="modal:lideres-cliente" inline />
        </DialogTitle>
        <DialogContent>
          {clError && <Alert severity="error" sx={{ mb: 2 }}>{clError}</Alert>}
          <Box mb={2}>
            <Autocomplete
              options={clOptions.filter(u =>
                !(clientLeadsDialog.project?.clientLeads || []).some(cl => {
                  const clid = cl.userId?.toString();
                  return clid === u._id || cl._id === u._id;
                })
              )}
              getOptionLabel={o => o.name ? `${o.name} ${o.lastName || ''}`.trim() + ` (${o.email})` : o.email}
              filterOptions={x => x}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              onInputChange={(_, val) => handleClSearch(val)}
              onChange={(_, val) => { if (val) handleAddClientLead(val); }}
              renderInput={params => (
                <TextField {...params} label="Buscar y asignar Líder Cliente" placeholder="Nombre o correo..." />
              )}
              noOptionsText="Sin resultados"
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          <List dense>
            {(clientLeadsDialog.project?.clientLeads || []).map(cl => {
              const name = cl.name || cl.email;
              const avatarChar = (name || '?')[0].toUpperCase();
              const clId = cl.userId?.toString();
              return (
                <ListItem key={clId || cl.email}
                  secondaryAction={
                    <Tooltip title="Quitar acceso">
                      <IconButton edge="end" size="small" color="error"
                        onClick={() => handleRemoveClientLead(clId)}>
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#2979FF', fontSize: 14 }}>{avatarChar}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    secondary={cl.name ? cl.email : null}
                  />
                </ListItem>
              );
            })}
            {(!clientLeadsDialog.project?.clientLeads?.length) && (
              <ListItem><ListItemText secondary="Sin líderes cliente asignados." /></ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClientLeadsDialog({ open: false, project: null })}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Milestones Dialog */}
      <Dialog open={milestonesDialog.open} onClose={() => { setMilestonesDialog({ open: false, project: null }); cancelMsEdit(); }} maxWidth="sm" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Hitos — {milestonesDialog.project?.name}
          <DevBadge id="modal:hitos-proyecto" inline />
        </DialogTitle>
        <DialogContent>
          {msError && <Alert severity="error" sx={{ mb: 2 }}>{msError}</Alert>}

          {/* Existing milestones list */}
          {(milestonesDialog.project?.milestones?.length > 0) && (
            <Box mb={2}>
              <List dense disablePadding>
                {milestonesDialog.project.milestones.map(ms => (
                  <ListItem key={ms._id}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, pr: 1 }}
                    secondaryAction={
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Editar hito">
                          <IconButton size="small"
                            color={msEditTarget?._id === ms._id ? 'primary' : 'default'}
                            onClick={() => msEditTarget?._id === ms._id ? cancelMsEdit() : startEditMilestone(ms)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar hito">
                          <IconButton size="small" color="error"
                            onClick={() => setMsConfirm({ open: true, milestoneId: ms._id })}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={500}>{ms.name}</Typography>
                          <Chip label={MILESTONE_STATUS_LABELS[ms.status] || ms.status}
                            size="small" color={MILESTONE_STATUS_COLORS[ms.status] || 'default'} />
                          {ms.dueDate && (
                            <Typography variant="caption" color="text.secondary">
                              Vence: {new Date(ms.dueDate).toLocaleDateString('es-CO')}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={ms.description || null}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}
          {(!milestonesDialog.project?.milestones?.length) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No hay hitos definidos aún.</Typography>
          )}

          {/* Create / edit form */}
          <Typography variant="subtitle2" gutterBottom>
            {msEditTarget ? 'Editar hito' : 'Nuevo hito'}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Nombre *" value={msForm.name}
              onChange={e => setMsForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Descripción" value={msForm.description}
              onChange={e => setMsForm(f => ({ ...f, description: e.target.value }))}
              multiline rows={2} fullWidth />
            <TextField select label="Estado" value={msForm.status}
              onChange={e => setMsForm(f => ({ ...f, status: e.target.value }))} fullWidth>
              {Object.entries(MILESTONE_STATUS_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            <TextField label="Fecha límite" type="date"
              value={msForm.dueDate}
              onChange={e => setMsForm(f => ({ ...f, dueDate: e.target.value }))}
              fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {msEditTarget && <Button onClick={cancelMsEdit}>Cancelar edición</Button>}
          <Button onClick={() => { setMilestonesDialog({ open: false, project: null }); cancelMsEdit(); }}>Cerrar</Button>
          <Button variant="contained" onClick={handleSaveMilestone}
            disabled={msSaving || !msForm.name.trim()}>
            {msSaving ? 'Guardando...' : (msEditTarget ? 'Actualizar' : 'Agregar hito')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={msConfirm.open} title="Eliminar Hito"
        message="¿Eliminar este hito? Las actividades relacionadas conservarán su referencia histórica."
        confirmLabel="Eliminar"
        onConfirm={() => handleDeleteMilestone(msConfirm.milestoneId)}
        onCancel={() => setMsConfirm({ open: false, milestoneId: null })}
      />
    </Box>
  );
}
